/**
 * 全站时间统一为 UTC+7（Asia/Bangkok）。
 *
 * 背景：
 * - DB 存的时间已经是 UTC+7 的「墙上时钟」值，但 JSON 序列化时可能带 'Z' 假装 UTC，也可能裸串。
 * - 浏览器在不同地区跑（中国 UTC+8、欧美等），dayjs() 默认走浏览器时区会和后端日切对不上。
 * - 此处统一：① 显示一律按 UTC+7 ② 「现在 / 今天」一律按 UTC+7
 *
 * 用法：
 *   import { formatToDateTime, formatUtcIsoToTz, formatToDate, nowTz, todayTz, TZ } from '@/utils/dateUtil'
 *   formatToDateTime(row.CreateDate)   // → '2026-05-28 14:30:00'（UTC+7 假 Z / 墙上时钟）
 *   formatUtcIsoToTz(row.StartTime)    // → 真实 UTC ISO 换算为 UTC+7 展示
 *   nowTz()                            // 现在的 UTC+7 dayjs 实例（可继续 .subtract / .add）
 *   todayTz()                          // 今天的 UTC+7 自然日开始（00:00:00）
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
const DATE_FORMAT = 'YYYY-MM-DD'

/** 项目硬编码时区：Asia/Bangkok = UTC+7（无夏令时） */
export const TZ = 'Asia/Bangkok'

/**
 * 展示时间：把任意输入（带 Z / 不带 Z / Date / 数字）统一按 UTC+7 显示墙上时钟。
 *
 * 实现：
 * - 字符串里如果带 'Z' 或 +HH:mm 偏移，dayjs 默认会按 UTC 解析→换算到浏览器本地。
 *   我们想要的是：把字符串里的「数字」当作 UTC+7 的墙上时钟原样展示。
 * - 解法：先 dayjs.utc(value) 当 UTC 解析（拿到一个 Date 对象，其 UTC 等于字符串数字），
 *        再 .tz(TZ) 切到 UTC+7 显示。
 *   但因为后端写入是 UTC+7 但 JSON 又标 Z（即原始数字就是 UTC+7 的墙上时钟），
 *   所以解析为 UTC、显示为 UTC+7 不行（会再加 7 小时）。
 *   正确做法：dayjs.utc(value).format(...) ——
 *   .utc(value) 把字符串数字视为 UTC 内部值，.format() 默认输出 UTC 数字（不加偏移），
 *   出来就是字符串里的原始数字，等于 UTC+7 墙上时钟。
 */
export function formatToDateTime(date?: dayjs.ConfigType, format = DATE_TIME_FORMAT): string {
  if (date === null || date === undefined || date === '') return ''
  return dayjs.utc(date).format(format)
}

export function formatToDate(date?: dayjs.ConfigType, format = DATE_FORMAT): string {
  if (date === null || date === undefined || date === '') return ''
  return dayjs.utc(date).format(format)
}

/**
 * 真实 UTC ISO 串（带 Z / 偏移）→ UTC+7 墙上时钟展示。
 * 用于后端已按 UTC 正确序列化的字段（如嘉年华 StartTime / EndTime），
 * 与 {@link formatToDateTime}（DB 假 Z、数字即 UTC+7 墙上时钟）区分。
 */
export function formatUtcIsoToTz(date?: dayjs.ConfigType, format = DATE_TIME_FORMAT): string {
  if (date === null || date === undefined || date === '') return ''
  return dayjs.utc(date).tz(TZ).format(format)
}

/**
 * 「现在」一律 UTC+7 —— 替代裸 `dayjs()`。
 *
 * 用法：
 *   nowTz()                       → 当前 UTC+7 时刻
 *   nowTz().subtract(29, 'day')   → 29 天前的 UTC+7 时刻
 *   nowTz().format('YYYY-MM-DD')  → UTC+7 今天日期串
 */
export function nowTz(): dayjs.Dayjs {
  return dayjs().tz(TZ)
}

/**
 * 「今天 00:00:00 UTC+7」—— 替代 `dayjs().startOf('day')`。
 */
export function todayTz(): dayjs.Dayjs {
  return nowTz().startOf('day')
}

export const dateUtil = dayjs
